/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Sagas for Calls Detail
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.4.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import { HttpStatus } from '../../modules/app/appConstants';
import {
    makeSelectCallsDetailTranslation,
    makeSelectTranslationCommon,
    makeSelectWizardTranslation,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { IWizardTranslation } from '../../modules/internationalization/types/wizards/wizard';
import { logBusinessEvent } from '../../modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../modules/logging/loggingConstants';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import { EWizardReportParam } from '../../modules/wizard/components/WizardReport/WizardReportConstants';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '../../modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '../../modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '../../modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
} from '../../modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '../../modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction, TWizardStepNames } from '../../modules/wizard/wizardTypes';
import { makeSelectIsFeatureWithVersionEnabled } from '../PageAuth/selectors/authSelectors';
import { callsDetailSetIsStepValid, callsDetailSetParams, callsDetailStorageSave } from './callsDetailActions';
import {
    CALLS_DETAIL_HANDLE_NEXT,
    CALLS_DETAIL_STORAGE_SAVE,
    CALLS_DETAIL_VALIDATE_START,
    ECallsDetailSteps,
} from './callsDetailConstants';
import { ICallsDetailState } from './callsDetailReducer';
import { makeSelectCallsDetailParams, makeSelectCallsDetailsOrderParams } from './callsDetailSelectors';
import { ICallsDetailOrderParams } from './callsDetailTypes';

export function* callsDetailStepInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    yield call(callsDetailStorageRestoreSaga);

    const allParams = yield select(makeSelectCallsDetailParams());
    const stepName: string = yield select(makeSelectWizardStepName());
    const { step: wizardMetaStep } = action.payload;

    switch (stepName) {
        case ECallsDetailSteps.Prepare:
            yield put(
                callsDetailSetParams({
                    chooseAccountTakenEnabled: wizardMetaStep.chooseAccountTakenEnabled ?? false,
                    formatsAvailable: wizardMetaStep.formatsAvailable ?? [],
                    minDate: wizardMetaStep.minDate,
                }),
            );
            if (!allParams.reportFormat && wizardMetaStep.formatsAvailable?.length) {
                yield put(callsDetailSetParams({ reportFormat: wizardMetaStep.formatsAvailable[0] }));
            }
            yield put(callsDetailStorageSave());
            break;
        case ECallsDetailSteps.Final:
            yield put(
                callsDetailSetParams({
                    basketCount: wizardMetaStep.basketCount ?? 0,
                }),
            );
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

export function* callsDetailStorageRestoreSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());

    const localStorageParams = localStorageState[basketSnapshotId] ?? {};

    const params: any = [
        'accountTakenCorporate',
        'accountTakenPersonal',
        'chooseAccountTakenEnabled',
        'dateFrom',
        'dateTo',
        'reportFormat',
    ].reduce((acc: any, cur: string) => {
        localStorageParams[cur] && (acc[cur] = localStorageParams[cur]);
        return acc;
    }, {});

    yield put(callsDetailSetParams(params));
}

export function* callsDetailStorageSaveSaga(): SagaIterator {
    const allParams = yield select(makeSelectCallsDetailParams());
    const params = {
        accountTakenCorporate: allParams.accountTakenCorporate,
        accountTakenPersonal: allParams.accountTakenPersonal,
        chooseAccountTakenEnabled: allParams.chooseAccountTakenEnabled,
        dateFrom: allParams.dateFrom,
        dateTo: allParams.dateTo,
        reportFormat: allParams.reportFormat,
    };

    yield put(wizardStorageSaveStepAction({ params }));
}

export function* callsDetailValidateSaga(): SagaIterator {
    let isStepValid = true;
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case ECallsDetailSteps.Prepare:
            isStepValid = yield call(callsDetailPrepareValidateSaga);
            break;
        case ECallsDetailSteps.Final:
            isStepValid = yield call(wizardReportValidateSaga);
            break;
    }

    yield put(callsDetailSetIsStepValid({ isStepValid }));
}

export function* callsDetailPrepareValidateSaga(): SagaIterator {
    let isStepValid = true;
    const params: ICallsDetailState = yield select(makeSelectCallsDetailParams());

    if (params.chooseAccountTakenEnabled && !params.accountTakenPersonal && !params.accountTakenCorporate)
        isStepValid = false;

    return isStepValid;
}

export function* callsDetailHandleNextSaga(): SagaIterator {
    const stepName: TWizardStepNames = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case ECallsDetailSteps.Prepare:
            const allParams: ICallsDetailState = yield select(makeSelectCallsDetailParams());
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_CALL_DETAILS_MASTER_TO_STEP_2,
                    type: ELogBusinessEventType.common,
                }),
            );
            yield put(
                wizardNextAction({
                    data: {
                        dateFrom: allParams.dateFrom,
                        dateTo: allParams.dateTo,
                    },
                }),
            );
            break;
        case ECallsDetailSteps.Final:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            const callsDetailParams: ICallsDetailOrderParams = yield select(makeSelectCallsDetailsOrderParams());
            const reportDestEmail: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
            );
            const reportNeedCompression: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.ZIP),
            );
            const reportCompressionPassword: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.ZIP_PASSWORD),
            );

            if (reportDestEmail.isEnabled) callsDetailParams.destEmail = reportDestEmail.value;
            if (reportDestEmail.isEnabled) callsDetailParams.needCompression = reportNeedCompression.isEnabled;
            if (reportCompressionPassword.isEnabled)
                callsDetailParams.compressionPassword = reportCompressionPassword.value;

            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_CALL_DETAILS_MASTER_CREATE_ORDER,
                    type: ELogBusinessEventType.common,
                }),
            );

            yield put(
                wizardNextAction({
                    data: {
                        ...callsDetailParams,
                    },
                    ...reportParams,
                }),
            );
            break;
    }
}

export function* callsDetailNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    if (stepName === ECallsDetailSteps.Final) {
        const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

        const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
            makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
        );

        if (isMegaPowersFeatureGreaterOrEqualThan2) {
            const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

            const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
            const goBack = () => breadCrumbGoBack(breadCrumbsData);

            yield put(
                modalOpen({
                    render: () =>
                        createElement(ModalMultipleOrders, {
                            isOpen: true,
                            orderOrOrders: orderId,
                            onClose: goBack,
                            onSuccessButtonClick: breadCrumbGoBack,
                            orderTypeTranlation: orderCreationModal.success.orderType.CALLS_DETAIL_REPORT,
                        }),
                }),
            );
        } else {
            yield put(wizardOpenModalSummaryAction({ orderId }));
        }
        yield put(
            logBusinessEvent({
                type: ELogBusinessEventType.wizard,
                eventName: ELogBusinessEventName.B2B_CALL_DETAILS_MASTER_CREATE_ORDER,
                orderId,
            }),
        );
    }
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardCallsDetailFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationCallsDetail = yield select(makeSelectCallsDetailTranslation());
        const wizardTranslation: IWizardTranslation = yield select(makeSelectWizardTranslation());
        title =
            translationCallsDetail.error.codes[errorCode] ||
            translationCommon.error.codes[errorCode] ||
            wizardTranslation.wizardModalError.default;
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

export function* callsDetailSagas(): SagaIterator {
    yield all([
        takeEvery(WIZARD_META_SUCCESS, callsDetailStepInitSaga),
        takeEvery([WIZARD_START_SUCCESS, CALLS_DETAIL_STORAGE_SAVE], callsDetailStorageSaveSaga),
        takeEvery(CALLS_DETAIL_VALIDATE_START, callsDetailValidateSaga),
        takeEvery(CALLS_DETAIL_HANDLE_NEXT, callsDetailHandleNextSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, callsDetailNextSuccessSaga),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardCallsDetailFailedSaga,
        ),
    ]);
}
